import {
  Box,
  Button,
  ChakraProps,
  Flex,
  Icon,
  Input,
  List,
  Popover,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import AppSelectOption from "@/features/line-sheet-sets/app-select-option";
import React, {
  ChangeEvent,
  MouseEventHandler,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import SVG from "react-inlinesvg";
import { isMobile } from "react-device-detect";
import AppSelectMobileSheet from "@/features/line-sheet-sets/app-select-mobile-sheet";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

type AppSelectSizeType = "medium" | "small";

export type AppSelectProps<T> = SingleSelectProps<T> | MultipleSelectProps<T>;

interface SelectProps<T> {
  name: string;
  placeholderText?: string;
  variant?: "normal" | "icon" | "chip";
  title?: string;
  value?: T | T[];
  idFunc?: (t: T) => any;
  options: AppSelectOption<T>[];
  onClick?: MouseEventHandler<HTMLButtonElement>;
  minWidth?: string;
  width?: string;
  height?: string;
  isActive?: boolean;
  isDisabled?: boolean;
  size?: AppSelectSizeType;
  // buttonRef?: React.Ref<HTMLButtonElement>;
  buttonRef?: React.ForwardedRef<HTMLButtonElement>;
  isSearchable?: boolean;
  // 옵션이 적어도 검색 입력을 항상 노출한다 (기본은 옵션 5개 초과일 때만 노출).
  alwaysSearchable?: boolean;
  isClearable?: boolean;
  isReadOnly?: boolean;
  isMultiple?: boolean;
  isLiteral?: boolean;
  requestComponent?: React.ReactNode;
  popoverStrategy?: "fixed" | "absolute";
  // 모바일에서 드롭다운을 전체화면 시트로 연다. 검색 인풋이 실제로 노출되는 셀렉터에만
  // 의미가 있다 (그 인풋 때문에 올라온 키보드가 팝오버를 가리는 것이 문제이므로).
  // 기본값 false — 넘기지 않은 호출부는 동작이 그대로다.
  mobileSheet?: boolean;
  outerOpen?: boolean;
  setOuterOpen?: (open: boolean) => void;
  customText?: string;
  customTrigger?: React.ReactNode;
  dropdownWidth?: string;
  showCount?: boolean;
  showSelectedBorder?: boolean;
  showEmptyDataOption?: boolean;
  borderWidth?: string;
  backgroundColor?: string;
  disableActive?: boolean;
}

interface SingleSelectProps<T> extends SelectProps<T> {
  value?: T;
  isMultiple?: false;
  onSelect?: (value?: T) => void;
}

interface MultipleSelectProps<T> extends SelectProps<T> {
  value?: T[];
  isMultiple: true;
  onSelect?: (value?: T[]) => void;
}

function calcSizeStyle(size: AppSelectSizeType): ChakraProps {
  let customStyle: ChakraProps = {};
  if (size === "medium") {
    customStyle = {
      p: "4px 12px",
    };
  } else if (size === "small") {
    customStyle = {
      p: "4px 8px",
    };
  }
  return customStyle;
}

function calcVariantStyle(
  borderWidth: string,
  isDisabled: boolean,
  isActive: boolean,
  isHovered: boolean,
  readOnly: boolean,
  disableActive: boolean,
  backgroundColor: string | undefined,
  variant: AppSelectProps<any>["variant"]
): ChakraProps {
  const defaultStyle: ChakraProps = {
    borderRadius: "4px",
    borderWidth: borderWidth,
    borderStyle: "solid",
    _hover: {},
  };

  let customStyle: ChakraProps = {};

  if (variant === "chip") {
    customStyle = {
      justifyContent: "flex-start",
      backgroundColor: "white",
      borderColor: "#8F8F8C",
    };
  }

  if (variant === "normal") {
    if (isDisabled) {
      customStyle = {
        borderColor: "#8F8F8C",
        backgroundColor: "#EFEFED",
        color: "#6F6F6C",
        cursor: "default",
      };
    } else if (disableActive) {
      customStyle = {
        ...customStyle,
        _active: {},
      };
    } else if (isActive) {
      customStyle = {
        borderColor: "#1272EF",
        backgroundColor: "#FFF",
        color: "#1272EF",
      };
    } else if (isHovered) {
      customStyle = {
        borderColor: "#444440",
        backgroundColor: "#FFF",
        color: "#444440",
        cursor: isDisabled || readOnly ? "default" : "pointer",
      };
    } else if (readOnly) {
      customStyle = {
        borderColor: "#6F6F6C",
        backgroundColor: "#FFF",
        color: "#6F6F6C",
        cursor: "default",
      };
    } else {
      customStyle = {
        borderColor: "#8F8F8C",
        backgroundColor: "#FFF",
        color: "#444440",
      };
    }
  } else if (variant === "icon") {
    if (isHovered) {
      customStyle = {
        color: "#1272EF",
        border: "none",
        backgroundColor: "transparent",
        _hover: {},
        _active: {
          background: "transparent",
        },
      };
    } else {
      customStyle = {
        color: "#8F8F8C",
        border: "none",
        borderWidth: "0px",
        backgroundColor: "transparent",
        _hover: {},
        _active: {
          background: "transparent",
        },
      };
    }
  }

  if (backgroundColor) {
    customStyle.backgroundColor = backgroundColor;
  }

  return {
    ...defaultStyle,
    ...customStyle,
  };
}

function isSingleSelect<T>(
  onSelect: ((value?: T) => void) | ((value?: T[]) => void),
  isMultiple: boolean
): onSelect is (value?: T) => void {
  return !isMultiple;
}

export default function AppSelect<T>({
  name,
  placeholderText = "",
  variant = "normal",
  title,
  value,
  idFunc = (t: T) => t,
  onSelect,
  options,
  minWidth,
  width,
  height,
  isActive = false,
  isDisabled = false,
  onClick = () => {},
  size = "medium",
  buttonRef,
  isSearchable = true,
  alwaysSearchable = false,
  isClearable = true,
  isReadOnly = false,
  isMultiple = false,
  isLiteral = false,
  requestComponent,
  popoverStrategy = "absolute",
  mobileSheet = false,
  outerOpen,
  setOuterOpen = () => {},
  customText,
  customTrigger,
  dropdownWidth,
  showCount,
  showSelectedBorder,
  showEmptyDataOption,
  disableActive = false,
  borderWidth = "0.5px",
  backgroundColor,
}: AppSelectProps<T>) {
  const [isHover, setIsHover] = useState<boolean>(false);
  const { isOpen, onToggle, onClose } = useDisclosure();
  const [queryValue, setQueryValue] = useState<string>("");
  const searchInputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();

  const customStyle = {
    ...calcVariantStyle(
      borderWidth,
      isDisabled,
      isActive,
      isHover,
      isReadOnly,
      disableActive,
      backgroundColor,
      variant
    ),
    ...calcSizeStyle(size),
  };

  // function handleClick(t: AppSelectOption<T>) {
  //   if (onSelect) {
  //     if (isMultiple) {
  //       //todo
  //     } else {
  //       onSelect(t.value);
  //     }
  //   }
  //   onClose();
  // }

  const calcIsActive = useCallback(
    (option: AppSelectOption<T>) => {
      if (value) {
        if (Array.isArray(value)) {
          return value.some((v) => idFunc(v) === idFunc(option.value));
        } else {
          return idFunc(value) === idFunc(option.value);
        }
      }
      return false;
    },
    [idFunc, value]
  );

  const handlePopoverClose = useCallback(() => {
    setQueryValue("");
    onClose();
    setOuterOpen(false);
  }, [onClose, setOuterOpen]);

  const handleClick = useCallback(
    (t: AppSelectOption<T>) => {
      if (onSelect) {
        if (isSingleSelect(onSelect, isMultiple)) {
          onSelect(t.value);
          handlePopoverClose();
        } else {
          const isActive = calcIsActive(t);
          if (isActive) {
            onSelect(
              (value as T[]).filter((v) => idFunc(v) !== idFunc(t.value))
            );
          } else {
            onSelect([...((value || []) as T[]), t.value]);
          }
          // handlePopoverClose();
        }
      }
    },
    [onSelect, isMultiple, handlePopoverClose, calcIsActive, value, idFunc]
  );

  const [searchParams] = useSearchParams();
  const brandQueryList = searchParams.getAll("brand_name__eq").map((v) => {
    try {
      return decodeURIComponent(v);
    } catch {
      return v;
    }
  });

  const filteredOptions = options.filter((option) => {
    if (queryValue.length > 0) {
      const lowerCasedQueryValue = queryValue.toLowerCase();
      // 이름뿐 아니라 유사 표기(keywords)로도 찾게 한다 — 브랜드는 시트/사용자가
      // 'EMPORIO ARMANI', '프라다' 처럼 정식명과 다른 표기로 부르는 일이 잦다.
      return (
        option.name.toLowerCase().includes(lowerCasedQueryValue) ||
        (option.keywords || []).some((keyword) =>
          keyword.toLowerCase().includes(lowerCasedQueryValue)
        )
      );
    } else {
      return true;
    }
  });

  const emptyDataQueryList = brandQueryList.filter(
    (query) => !filteredOptions.find((option) => option.value === query)
  );
  const hasEmptyQuery = emptyDataQueryList.length !== 0;
  const showEmpty = showEmptyDataOption && hasEmptyQuery;

  const emptyQueryAddedFilteredOptions = showEmpty
    ? [
        ...emptyDataQueryList.map((item) => ({
          name: item + " (결과 없음)",
          value: item,
        })),
        ...filteredOptions,
      ]
    : filteredOptions;

  const onCustomToggle: MouseEventHandler<HTMLButtonElement> = (event) => {
    event.stopPropagation();
    if (!isDisabled && !isReadOnly) {
      onClick(event);
      setOuterOpen(true);
      if (options.length > 0) {
        onToggle();
      }
    }
  };

  function handleChangeQueryValue(e: ChangeEvent<HTMLInputElement>) {
    setQueryValue(e.target.value);
  }

  // const triggerName = useMemo(() => {
  //   if (value || value === 0) {
  //     if (Array.isArray(value)) {
  //       if (value.length > 0) {
  //         const first = value[0];
  //         const option = options.find(
  //           (option) => idFunc(option.value) === idFunc(first)
  //         );
  //
  //         if (option) {
  //           return `${option.name}${value.length > 1 ? " ,..." : ""}`;
  //         }
  //       }
  //     } else {
  //       const option = options.find(
  //         (option) => idFunc(option.value) === idFunc(value)
  //       );
  //       if (option) {
  //         return option.component || option.name;
  //       }
  //     }
  //   }
  //   return name;
  // }, [idFunc, name, options, value]);

  const triggerComponent = useMemo(() => {
    if (isMultiple && showCount && Array.isArray(value)) {
      return (
        <Text>
          {name} {value?.length}
        </Text>
      );
    }
    if (customText) {
      return <Text>{customText}</Text>;
    }
    if (value || value === 0) {
      if (Array.isArray(value)) {
        if (value.length > 0) {
          const first = value[0];
          const option = options.find(
            (option) => idFunc(option.value) === idFunc(first)
          );

          if (option) {
            return (
              <Text>{`${option.name}${value.length > 1 ? " ,..." : ""}`}</Text>
            );
          }
        }
      } else {
        const option = options.find(
          (option) => idFunc(option.value) === idFunc(value)
        );
        if (option) {
          return option.component || <Text>{option.name}</Text>;
        }
      }
    }
    return <Text>{name}</Text>;
  }, [customText, idFunc, name, options, value, isMultiple, showCount]);

  const isSearchableInternal = useMemo(() => {
    return options.length > 5;
  }, [options.length]);

  // 검색 인풋이 실제로 화면에 뜨는가. 이게 false면 키보드가 올라올 일이 없으므로
  // 모바일에서도 팝오버를 그대로 쓴다.
  const showsSearchInput =
    (isSearchableInternal || alwaysSearchable) && isSearchable;
  const useMobileSheet = mobileSheet && isMobile && showsSearchInput;

  const [matchWidth, setMatchWidth] = useState<boolean>(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  // 검색 → ↓ 키로 결과 목록의 첫 항목으로 포커스 이동하기 위한 ref
  const listRef = useRef<HTMLUListElement>(null);

  // 검색 input의 ↓ 키 처리: 첫 옵션(button) 포커스
  const handleSearchKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      const firstButton = listRef.current?.querySelector(
        "button"
      ) as HTMLElement | null;
      firstButton?.focus();
    }
  };

  // 검색 input에서 드래그 selection 중 마우스가 input 밖으로 나가도 selection이 시각적으로 풀리지 않게 한다.
  // 핵심: setPointerCapture로 모든 pointer 이벤트를 input이 받도록 캡처 → 브라우저가 input 내부 selection을
  // 자연스럽게 계속 확장한다 (focus가 다른 곳으로 옮겨가지 않음).
  // pointer capture는 pointerup 시점에 자동 해제되므로 별도 해제 코드 불필요.
  const handleSearchPointerDown = (
    event: React.PointerEvent<HTMLInputElement>
  ) => {
    event.stopPropagation();
    const input = event.currentTarget as HTMLInputElement;
    try {
      input.setPointerCapture(event.pointerId);
    } catch {
      // 일부 환경에서 실패할 수 있으나 그 경우 브라우저 기본 동작에 맡긴다.
    }
  };

  useLayoutEffect(() => {
    // if (name === "라인 시트") {
    //   console.dir({
    //     trigger: triggerRef.current?.clientWidth,
    //     content: contentRef.current?.clientWidth,
    //     matchWidth,
    //   });
    // }

    if (
      !matchWidth &&
      triggerRef.current &&
      contentRef.current &&
      options.length > 0 &&
      width === undefined
    ) {
      if (triggerRef.current.clientWidth >= contentRef.current.clientWidth) {
        setMatchWidth(true);
      }
    }
  }, [width, options.length, matchWidth]);

  if (isLiteral) {
    // if (React.isValidElement(value)) {
    //   return value;
    // }
    // return <Text>{AppUtils.isTrue(value) ? triggerName : ""}</Text>;
    return triggerComponent;
  }

  // 트리거 버튼. 모바일 시트 경로에서는 PopoverTrigger 없이 단독으로 렌더해야 하므로
  // 변수로 뽑아 두 경로가 같은 버튼을 쓰게 한다.
  const triggerButton = (
    <Button
      ref={triggerRef}
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
      fontSize={"12px"}
      minH={"0"}
      minWidth={minWidth}
      width={width || "auto"}
      height={height || "28px"}
      variant={"ghost"}
      border={
        showSelectedBorder &&
        isMultiple &&
        Array.isArray(value) &&
        value.length > 0
          ? "1px solid #1272EF !important"
          : ""
      }
      onClick={onCustomToggle}
      {...customStyle}
    >
      {customTrigger && <Flex>{customTrigger}</Flex>}
      {variant === "normal" && !customTrigger && (
        <Flex
          width={"100%"}
          flexDirection={"row"}
          alignItems={"center"}
          gap={"8px"}
          overflow={"hidden"}
          textOverflow={"ellipsis"}
          whiteSpace={"nowrap"}
          position={"relative"}
          backgroundColor={"inherit"}
          pr={"16px"}
          height={"100%"}
        >
          {triggerComponent}
          {!isReadOnly && (
            <Box
              position={"absolute"}
              right={"0"}
              backgroundColor={"inherit"}
              paddingLeft={"4px"}
            >
              <Icon
                right={"0"}
                as={SVG}
                color={"inherit"}
                src={"/icons/icon_polygon_1.svg"}
                boxSize={"8px"}
              />
            </Box>
          )}

          {value != undefined && !isDisabled && isClearable && !isReadOnly && (
            <Box
              alignItems={"center"}
              position={"absolute"}
              right={"8px"}
              backgroundColor={"inherit"}
              display={isHover ? "flex" : "flex"}
              padding={"0 6px 0 4px"}
              minW={"0"}
              height={"16px"}
              _hover={{}}
              borderRadius={"0"}
              onClick={(event) => {
                event.stopPropagation();
                if (onSelect) {
                  onSelect(undefined);
                  handlePopoverClose();
                }
              }}
              aria-label={"clear"}
            >
              <Icon
                right={"0"}
                as={SVG}
                color={isHover ? "inherit" : "#8F8F8C"}
                src={"/icons/icon_x_letter.svg"}
                boxSize={"10px"}
              />
            </Box>
          )}
        </Flex>
      )}
      {variant === "chip" && !customTrigger && (
        <Flex
          width={"100%"}
          flexDirection={"row"}
          alignItems={"center"}
          gap={"8px"}
          overflow={"hidden"}
          textOverflow={"ellipsis"}
          whiteSpace={"nowrap"}
          position={"relative"}
          backgroundColor={"inherit"}
          pr={"8px"}
          height={"100%"}
        >
          <Box
            position={"absolute"}
            right={"0"}
            backgroundColor={"inherit"}
            paddingLeft={"4px"}
          >
            <Icon
              right={"0"}
              as={SVG}
              color={"inherit"}
              src={"/icons/icon_polygon_1.svg"}
              boxSize={"8px"}
            />
          </Box>
          {value && Array.isArray(value) && value.length > 0 ? (
            <Flex flexDirection={"row"} gap={"6px"}>
              {value.map((option) => {
                const brand = options.find(
                  (opt) => idFunc(opt.value) === idFunc(option)
                );
                if (!brand) return null;
                return (
                  <Flex
                    key={brand.name}
                    alignItems={"center"}
                    padding={"4px 8px"}
                    background={"#F7F9FA"}
                    borderRadius={"8px"}
                    fontSize={"12px"}
                    color={"#1272EF"}
                  >
                    <Text>{brand.name}</Text>
                    <Box
                      as={SVG}
                      src={"/icons-v1_1/x.svg"}
                      width={"16px"}
                      height={"16px"}
                      marginLeft={"4px"}
                      cursor={"pointer"}
                      onClick={(event: {
                        preventDefault: () => void;
                        stopPropagation: () => void;
                      }) => {
                        event.preventDefault();
                        event.stopPropagation();
                        if (onSelect && !isSingleSelect(onSelect, isMultiple)) {
                          onSelect(
                            (value as T[]).filter(
                              (v) => idFunc(v) !== idFunc(brand.value)
                            )
                          );
                        }
                      }}
                    />
                  </Flex>
                );
              })}
            </Flex>
          ) : (
            <Text color={"#1272EF"} textStyle={"body-md-regular"}>
              {placeholderText || name}
            </Text>
          )}
        </Flex>
      )}
      {variant === "icon" && (
        <SVG
          color={"inherit"}
          src={"/icons/icon_hamburger_menu.svg"}
          width={"14px"}
          height={"14px"}
        />
      )}
    </Button>
  );

  // 모바일에서 검색 인풋이 뜨는 셀렉터는 팝오버 대신 전체화면 시트로 연다.
  // 카카오톡 인앱브라우저의 바텀시트 모드(알림톡/봇 대화방 링크)는 가상 키보드가 떠도
  // 뷰포트를 조정하지 않아, 트리거 아래에 붙는 팝오버가 통째로 키보드에 가려진다.
  // 근거와 실측치는 app-select-mobile-sheet.tsx 주석 참고.
  if (useMobileSheet) {
    return (
      <>
        {triggerButton}
        {(outerOpen || isOpen) && (
          <AppSelectMobileSheet<T>
            isOpen={true}
            onClose={handlePopoverClose}
            title={title ? title : name}
            // showEmptyDataOption이 끼워 넣는 "결과 없음" 항목은 value가 T가 아니라
            // 쿼리 문자열이라 유니온 타입이 된다. 팝오버 경로도 map 콜백을 any로 받아
            // 같은 취급을 하고 있다(아래 emptyQueryAddedFilteredOptions.map 참고).
            options={emptyQueryAddedFilteredOptions as AppSelectOption<T>[]}
            queryValue={queryValue}
            onQueryChange={setQueryValue}
            idFunc={idFunc}
            calcIsActive={calcIsActive}
            onClickOption={handleClick}
            isDisabled={isDisabled}
            isMultiple={isMultiple}
            requestComponent={requestComponent}
            emptyStyledCount={showEmpty ? emptyDataQueryList.length : 0}
          />
        )}
      </>
    );
  }

  return (
    <Popover
      placement={"bottom-start"}
      flip={true}
      isOpen={outerOpen || isOpen}
      onClose={handlePopoverClose}
      initialFocusRef={searchInputRef}
      // boundary={"clippingParents"}
      strategy={popoverStrategy}
      preventOverflow={true}
      matchWidth={matchWidth}
    >
      <PopoverTrigger>{triggerButton}</PopoverTrigger>
      <PopoverContent
        ref={contentRef}
        width={
          dropdownWidth
            ? dropdownWidth
            : variant === "icon" && width
            ? width
            : "auto"
        }
        overflow={"hidden"}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            event.stopPropagation();
            handlePopoverClose();
          }
        }}
      >
        <PopoverHeader background={"#F7F9FA"} fontSize={"12px"}>
          <Text>{title ? title : name}</Text>
          <PopoverCloseButton />
        </PopoverHeader>
        <PopoverBody p={"0"}>
          {showsSearchInput && (
            <Input
              type={"text"}
              placeholder={t("search")}
              fontSize={"12px"}
              border={"none"}
              borderRadius={"0"}
              padding={"8px"}
              height={"auto"}
              value={queryValue}
              onChange={handleChangeQueryValue}
              onKeyDown={handleSearchKeyDown}
              onPointerDown={handleSearchPointerDown}
              borderBottom={"0.5px solid #D4D4D1"}
              autoComplete={"off"}
              ref={searchInputRef}
            />
          )}
          <List
            ref={listRef}
            display={"flex"}
            flexDirection={"column"}
            fontSize={"12px"}
            maxH={"172px"}
            overflowY={"auto"}
          >
            {emptyQueryAddedFilteredOptions.length === 0 && (
              <Flex
                paddingY={"16px"}
                flexDirection={"column"}
                alignItems={"center"}
                gap={"12px"}
              >
                <Flex flexDirection={"column"} alignItems={"center"}>
                  <Box color={"#D4D4D1"}>
                    <SVG
                      src={"/icons-v1_1/warning-triangle-solid.svg"}
                      width={28}
                      height={28}
                    />
                  </Box>
                  <Text textStyle={"body-md-regular"} color={"#6F6F6C"}>
                    {t("no_results")}
                  </Text>
                </Flex>
                {requestComponent}
              </Flex>
            )}
            {emptyQueryAddedFilteredOptions.map((option: any, index) => {
              return (
                <AppSelectOption<T>
                  key={idFunc(option.value)}
                  option={option}
                  isActive={calcIsActive(option)}
                  handleClick={handleClick}
                  isDisabled={isDisabled}
                  itemStyle={
                    showEmpty && index < emptyDataQueryList.length
                      ? { color: "#C0C0C0", whiteSpace: "nowrap" }
                      : {}
                  }
                />
              );
            })}
          </List>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
}
